import { goto } from '$app/navigation';
import { MutationCache, QueryCache } from '$lib/api/tanstack-query';

import {
  isLoginMethodDeniedError,
  isNonRetryableQueryError,
  parseLoginMethodDeniedError,
} from '$lib/api/errors';

const LOGIN_METHOD_DENIED_PATH = '/login-method-denied';

/**
 * Denied auth types that mean the session login method is not yet available on the
 * JWT (cold start / pre-migration). Force a fresh interactive login instead of
 * showing the denial page.
 */
const RELOGIN_REQUIRED_AUTH_TYPES = new Set(['SESSION_LOGIN_METHOD_UNSET']);

let redirectInFlight = false;

const loginMethodDeniedPath = (error: unknown): string | undefined => {
  if (!isLoginMethodDeniedError(error)) {
    return undefined;
  }
  const details = parseLoginMethodDeniedError(error);
  if (details === null) {
    return LOGIN_METHOD_DENIED_PATH;
  }
  const params = new URLSearchParams({
    org: details.orgId,
    method: details.authType,
  });
  return `${LOGIN_METHOD_DENIED_PATH}?${params.toString()}`;
};

/** TanStack Query default retry count for recoverable (retryable) errors. */
const DEFAULT_QUERY_RETRY_COUNT = 3;

/**
 * Retry only recoverable failures. Permission errors and other non-retryable
 * gRPC codes fail immediately — gRPC-web reports them as HTTP 200, so HTTP
 * status must not be used to decide retryability.
 */
export const shouldRetryQueryOnError = (
  failureCount: number,
  error: unknown
): boolean => {
  if (isNonRetryableQueryError(error)) {
    return false;
  }
  return failureCount < DEFAULT_QUERY_RETRY_COUNT;
};

/**
 * Builds the global TanStack Query error handler that reacts to
 * login-method-denied errors.
 *
 * For a genuine denial (the user's session login method isn't allowed), it
 * redirects to the denial page. When sessionLoginMethod is absent
 * ({@link RELOGIN_REQUIRED_AUTH_TYPES}), it forces logout so the next login
 * mints a JWT with the session method populated.
 */
export const createLoginMethodDeniedErrorHandler =
  (logout: () => unknown) =>
  (error: unknown): void => {
    if (
      redirectInFlight ||
      globalThis.location.pathname === LOGIN_METHOD_DENIED_PATH
    ) {
      return;
    }
    if (!isLoginMethodDeniedError(error)) {
      return;
    }

    const details = parseLoginMethodDeniedError(error);
    if (details !== null && RELOGIN_REQUIRED_AUTH_TYPES.has(details.authType)) {
      redirectInFlight = true;
      logout();
      return;
    }

    const path = loginMethodDeniedPath(error);
    if (path === undefined) {
      return;
    }
    redirectInFlight = true;
    void goto(path).finally(() => {
      redirectInFlight = false;
    });
  };

export const createLoginMethodDeniedQueryCache = (
  logout: () => unknown
): QueryCache =>
  new QueryCache({ onError: createLoginMethodDeniedErrorHandler(logout) });

export const createLoginMethodDeniedMutationCache = (
  logout: () => unknown
): MutationCache =>
  new MutationCache({ onError: createLoginMethodDeniedErrorHandler(logout) });
